import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, {
  useCallback, useContext, useEffect, useId,
} from 'react';
import {
  useLocalStorage,
} from 'react-use';
import BreakpointContext from '../../context/breakpoint';
import useDigitalData from '../../hooks/useDigitalData';
import useTranslatedText from '../../hooks/useTranslatedText';
import { RECENT_SEARCHES_KEY } from '../../tools/constants';
import handleAlgonomyProductClick from '../../tools/handleAlgonomyProductClick';
import removeFalsyItems from '../../tools/removeFalsyItems';
import $window from '../../tools/window';
import BubbleRecommendationsWrapper from '../BubbleRecommendationsWrapper/BubbleRecommendationsWrapper';
import { DD_SEARCH_FLYOUT_CATEGORY_BUBBLES_SUGGESTIONS_TEST } from '../DigitalDataProvider';
import PdpSuggestedSearches from '../PdpSuggestedSearches/PdpSuggestedSearches';
import Recommendations from '../Recommendations/Recommendations';
import recsStyles from '../Recommendations/recommendations.module.scss';
import useFeatureFlags from '../SearchPage/hooks/useFeatureFlags';
import SearchRecommendations from '../SearchRecommendations';
import { usePopularSearches } from '../SearchWrapper/hooks/useQueries';
import style from '../SearchWrapper/searchWrapper.module.scss';
import HelpfulLinks from './HelpfulLinks';
import SearchDefaultButtonGroup from './SearchDefaultButtonGroup';
import listStyles from './SearchDefaultView.module.scss';

export default function SearchDefaultView({
  clickableSearchChoiceHandler = () => { },
  isFormFocused = false,
  clientId,
  showRecsContainers,
  searchDefaultViewPlace,
  placementIds = '',
}) {
  const popularSearchHeader = useTranslatedText('popularSearchesHeader', { fallback: 'Popular Searches' });
  const recentSearchesLabel = useTranslatedText('recentSearchesLabel', { fallback: 'Recent Searches' });
  const searchLabel = useTranslatedText('autoSuggestDesktopPlaceholder', { fallback: 'Search' });
  const { large: isDesktop } = useContext(BreakpointContext);
  // adding dynamic id because this component can be on the page multiple times
  const uniqueId = useId();
  const recsTitle = useTranslatedText('topCatsAndCollections', { fallback: 'Top Categories & Collections' });

  const [recentSearchesList, setRecentSearchesList] = useLocalStorage(RECENT_SEARCHES_KEY, []);
  const popularSearchesList = usePopularSearches();
  const { quickviewMfeCatalogSearchFlyout } = useFeatureFlags();

  const handleProductClick = useCallback((event, product = {}) => {
    handleAlgonomyProductClick(event, product, quickviewMfeCatalogSearchFlyout);
  }, [quickviewMfeCatalogSearchFlyout]);

  const onSubmit = useCallback(({ type, key, currentTarget }) => {
    const { dataset } = currentTarget || {};
    const { type: dataType } = dataset || {};
    dataset.value = dataset.value || currentTarget.value;

    if (type === 'click' || key === 'Enter') {
      clickableSearchChoiceHandler(dataset, dataType, searchDefaultViewPlace);
    }
  }, [clickableSearchChoiceHandler, searchDefaultViewPlace]);

  const {
    [DD_SEARCH_FLYOUT_CATEGORY_BUBBLES_SUGGESTIONS_TEST]: hasSearchFlyoutCategoryBubblesTestEnabled,
  } = useDigitalData([
    DD_SEARCH_FLYOUT_CATEGORY_BUBBLES_SUGGESTIONS_TEST,
  ]);
  useEffect(() => {
    const handlePdpSearchSuggestionClick = (event) => {
      onSubmit(event.detail.event);
    };

    $window.addEventListener('pdp-suggested-search-clicked', handlePdpSearchSuggestionClick);
    return () => {
      $window.removeEventListener('pdp-suggested-search-clicked', handlePdpSearchSuggestionClick);
    };
  }, [onSubmit]);

  const removeRecentSearches = (event) => {
    if (event.type === 'click' || event.key === 'Enter') {
      setRecentSearchesList([]);
    }
  };

  const listData = [
    recentSearchesList.length > 0 && {
      clearAllButtonHandler: removeRecentSearches,
      dataType: 'recent',
      groupId: 'recent-searches-group',
      hasClearAllButton: true,
      headingLabel: recentSearchesLabel?.value,
      options: recentSearchesList.map(
        (item) => ({
          key: item,
          title: item,
        }),
      ),
      eventHandler: onSubmit,
    },
    popularSearchesList.length > 0 && {
      dataType: 'popular',
      groupId: 'popular-searches-group',
      headingLabel: popularSearchHeader?.value,
      options: popularSearchesList.map(
        (item) => ({
          key: item,
          title: item,
        }),
      ),
      eventHandler: onSubmit,
    },
  ];

  return (
    <div className={listStyles.searchDefaultViewContainer}>
      <div
        aria-label={searchLabel.value}
        className={classNames(
          listStyles.listContainer,
          listStyles.sectionWrapper,
          listStyles.listWrapper,
          'auto-suggest-list-menu-wrapper',
          'fixed-list-container',
        )}
        data-dynamic-suggestions="false"
        data-expanded={!!isFormFocused}
        data-focused={!!isFormFocused}
        id={`search-default-view-${uniqueId}`}
        role="listbox"
        tabIndex={-1}
      >
        {removeFalsyItems(listData).map((list) => (
          <SearchDefaultButtonGroup
            key={list.dataType}
            clearAllButtonHandler={list.clearAllButtonHandler}
            dataType={list.dataType}
            eventHandler={list.eventHandler}
            groupId={list.groupId}
            hasClearAllButton={list.hasClearAllButton}
            headingLabel={list.headingLabel}
            options={list.options}
          />
        ))}
        {isDesktop ? <HelpfulLinks /> : null}
      </div>

      <div className={style['recs-container']}>
        {showRecsContainers ? (
          <>
            <PdpSuggestedSearches />
            {hasSearchFlyoutCategoryBubblesTestEnabled ? (
              <BubbleRecommendationsWrapper
                queryId="flyoutBubbleRecommendations"
                skipQuery
              />
            ) : (
              <div className={style['links-container']}>
                <SearchRecommendations
                  isBubbleStyle
                  recsTitle={recsTitle?.value}
                />
              </div>
            )}
            <Recommendations
              clientId={clientId}
              handleOnProductClick={handleProductClick}
              placementIds={placementIds}
              styles={`${recsStyles.recsWrapper}`}
            />
          </>
        ) : null}
      </div>
      {!isDesktop ? (
        <div
          aria-label={searchLabel.value}
          className={classNames(
            listStyles.listContainer,
            listStyles.sectionWrapper,
            listStyles.listWrapper,
            'auto-suggest-list-menu-wrapper',
            'fixed-list-container',
          )}
          data-dynamic-suggestions="false"
          data-expanded={!!isFormFocused}
          data-focused={!!isFormFocused}
          id={`mobile-helpful-links-${uniqueId}`}
          role="listbox"
          tabIndex={-1}
        >
          <HelpfulLinks />
        </div>
      ) : null}
    </div>
  );
}

SearchDefaultView.propTypes = {
  clickableSearchChoiceHandler: PropTypes.func.isRequired,
  searchDefaultViewPlace: PropTypes.string.isRequired,
  isFormFocused: PropTypes.bool,
  clientId: PropTypes.string,
  showRecsContainers: PropTypes.bool,
  placementIds: PropTypes.string,
};
